import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import Button from 'shopper/components/Button';
import Input from 'shopper/components/Input';
import Textarea from 'shopper/components/Textarea';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import {
  isEmailValid,
  isPhoneValid,
  phoneMask,
  removeNonDigits,
} from 'lib/string';

import { postCompanyInterest } from 'services/mail';

const RULES = {
  FIELD_REQUIRED: 'Esse campo precisa ser preenchido',
  EMAIL_NOT_VALID: 'Digite um endereço de e-mail válido',
  PHONE_NOT_VALID: 'Digite um telefone válido',
};

const Form = ({
  className,
  gaEventCategory,
  submitButtonProps,
  withWordsCounter = false,
  onSuccess,
}) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
    },
    reValidateMode: 'onBlur',
  });

  const onSubmit = async (data) => {
    sendEvent({
      category: gaEventCategory,
      action: 'submit',
    });

    await postCompanyInterest({ ...data, phone: removeNonDigits(data.phone) });

    onSuccess();
    reset();
  };

  return (
    <form
      className={twJoin('flex w-full flex-col gap-y-6 md:max-w-sm', className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState: { error } }) => (
          <Input
            error={!!error}
            label={placeholder('inputs.labels.name')}
            message={error?.message}
            placeholder={placeholder('inputs.placeholders.name')}
            full
            required
            onFocus={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'name_focus',
              });
            }}
            {...field}
          />
        )}
        rules={{
          required: RULES.FIELD_REQUIRED,
        }}
      />
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <Input
            error={!!error}
            label={placeholder('inputs.labels.email')}
            message={error?.message}
            placeholder={placeholder('inputs.placeholders.email')}
            type="email"
            full
            required
            onFocus={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'email_focus',
              });
            }}
            {...field}
          />
        )}
        rules={{
          required: RULES.FIELD_REQUIRED,
          validate: (value) => isEmailValid(value) || RULES.EMAIL_NOT_VALID,
        }}
      />
      <Controller
        control={control}
        name="phone"
        render={({
          field: { value, onChange, ...fieldProps },
          fieldState: { error },
        }) => (
          <Input
            error={!!error}
            label={placeholder('inputs.labels.phone')}
            message={error?.message}
            placeholder={placeholder('inputs.placeholders.phone')}
            type="tel"
            value={phoneMask(value.toString())}
            full
            onChange={({ target }) => onChange(phoneMask(target.value))}
            onFocus={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'phone_focus',
              });
            }}
            {...fieldProps}
          />
        )}
        rules={{
          validate: (value) =>
            value.length > 0
              ? isPhoneValid(value) || RULES.PHONE_NOT_VALID
              : true,
        }}
      />
      <Controller
        control={control}
        name="company"
        render={({ field, fieldState: { error } }) => (
          <Input
            error={!!error}
            label={placeholder('inputs.labels.company')}
            message={error?.message}
            placeholder={placeholder('inputs.placeholders.company')}
            full
            onFocus={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'company_focus',
              });
            }}
            {...field}
          />
        )}
      />
      <Controller
        control={control}
        name="message"
        render={({ field: { value, ...rest }, fieldState: { error } }) => (
          <Textarea
            className={(!error || !withWordsCounter) && '[&>p]:text-end'}
            error={!!error}
            label={placeholder('inputs.labels.message')}
            maxLength={200}
            message={
              error || !withWordsCounter
                ? error?.message
                : `${value.length}/200`
            }
            placeholder={placeholder('inputs.placeholders.message')}
            value={value}
            full
            onFocus={() => {
              sendEvent({
                category: gaEventCategory,
                action: 'message_focus',
              });
            }}
            {...rest}
          />
        )}
      />
      <Button htmlType="submit" full {...submitButtonProps} />
    </form>
  );
};

Form.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  submitButtonProps: PropTypes.shape({
    children: PropTypes.string.isRequired,
  }).isRequired,
  withWordsCounter: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired,
};

export default Form;
